import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { 
  Business, 
  TrendingUp, 
  CheckCircle, 
  Schedule,
  Search,
  AttachMoney,
  AccountBalance
} from '@mui/icons-material';

const branchOptions = [
  { value: '', label: '-- Select Branch --' },
  { value: 'Main Branch', label: 'Main Branch' },
  { value: 'North Branch', label: 'North Branch' },
  { value: 'South Branch', label: 'South Branch' },
  { value: 'East Branch', label: 'East Branch' },
  { value: 'West Branch', label: 'West Branch' },
];

const sampleNewBookingData = [
  {
    sno: 1,
    branch: 'Main Branch',
    newBooking: 15,
    totalAmount: 37500000,
  },
  {
    sno: 2,
    branch: 'North Branch',
    newBooking: 12,
    totalAmount: 30000000,
  },
  {
    sno: 3,
    branch: 'South Branch',
    newBooking: 18,
    totalAmount: 45000000,
  },
  {
    sno: 4,
    branch: 'East Branch',
    newBooking: 10,
    totalAmount: 25000000,
  },
  {
    sno: 5,
    branch: 'West Branch',
    newBooking: 14,
    totalAmount: 35000000,
  },
];

const sampleCashChequeData = [
  {
    sno: 1,
    branch: 'Main Branch',
    cashCollection: 5500000,
    chequeOtherCollection: 8500000,
  },
  {
    sno: 2,
    branch: 'North Branch',
    cashCollection: 4200000,
    chequeOtherCollection: 7800000,
  },
  {
    sno: 3,
    branch: 'South Branch',
    cashCollection: 6800000,
    chequeOtherCollection: 9200000,
  },
  {
    sno: 4,
    branch: 'East Branch',
    cashCollection: 3500000,
    chequeOtherCollection: 6500000,
  },
  {
    sno: 5,
    branch: 'West Branch',
    cashCollection: 4800000,
    chequeOtherCollection: 7200000,
  },
];

const sampleChequeStatusData = [
  {
    sno: 1,
    branch: 'Main Branch',
    chequeCleared: 25,
    chequeBouncedAmount: 3,
  },
  {
    sno: 2,
    branch: 'North Branch',
    chequeCleared: 20,
    chequeBouncedAmount: 2,
  },
  {
    sno: 3,
    branch: 'South Branch',
    chequeCleared: 28,
    chequeBouncedAmount: 4,
  },
  {
    sno: 4,
    branch: 'East Branch',
    chequeCleared: 18,
    chequeBouncedAmount: 1,
  },
  {
    sno: 5,
    branch: 'West Branch',
    chequeCleared: 22,
    chequeBouncedAmount: 2,
  },
];

function BusinessSummaryReport() {
  const [filters, setFilters] = useState({
    branch: '',
    fromDate: '',
    toDate: '',
    searchTerm: ''
  });
  
  const [newBookingData] = useState(sampleNewBookingData);
  const [cashChequeData] = useState(sampleCashChequeData);
  const [chequeStatusData] = useState(sampleChequeStatusData);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    // Implement search logic here
    console.log('Search criteria:', filters);
  };

  const formatCurrency = (amount) => {
    return 'Rs. ' + new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Calculation functions for totals
  const getTotalNewBookings = () => {
    return newBookingData.reduce((total, item) => total + item.newBooking, 0);
  };

  const getTotalBookingAmount = () => {
    return newBookingData.reduce((total, item) => total + item.totalAmount, 0);
  };

  const getTotalCashCollection = () => {
    return cashChequeData.reduce((total, item) => total + item.cashCollection, 0);
  };

  const getTotalChequeOtherCollection = () => {
    return cashChequeData.reduce((total, item) => total + item.chequeOtherCollection, 0);
  };

  const getTotalChequeCleared = () => {
    return chequeStatusData.reduce((total, item) => total + item.chequeCleared, 0);
  };

  const getTotalChequeBounced = () => {
    return chequeStatusData.reduce((total, item) => total + item.chequeBouncedAmount, 0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Business Summary Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive business performance and financial overview
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Business Summary Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select 
                  value={filters.branch} 
                  onChange={(e) => handleFilterChange('branch', e.target.value)} 
                  label="Branch"
                >
                  {branchOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table 1: Total New Booking */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Total New Booking
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Branch</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>New Booking</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Total Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {newBookingData.map((booking, index) => (
                  <TableRow
                    key={booking.sno}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {booking.branch}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center', fontFamily: 'monospace' }}>
                      {booking.newBooking}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace' }}>
                      Rs. {(booking.totalAmount / 100000).toFixed(2)}L
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Total Row */}
                <TableRow sx={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '16px' }}>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', fontFamily: 'monospace', fontSize: '16px' }}>
                    <strong>{getTotalNewBookings()}</strong>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px' }}>
                    <strong>Rs. {(getTotalBookingAmount() / 100000).toFixed(2)}L</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Table 2: Cash and Cheque Collection */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Cash and Cheque Collection
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Branch</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Cash Collection</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Cheque/RTGS/NEFT/Other Collection</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cashChequeData.map((collection, index) => (
                  <TableRow
                    key={collection.sno}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {collection.branch}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace' }}>
                      Rs. {(collection.cashCollection / 100000).toFixed(2)}L
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace' }}>
                      Rs. {(collection.chequeOtherCollection / 100000).toFixed(2)}L
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Total Row */}
                <TableRow sx={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '16px' }}>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px' }}>
                    <strong>Rs. {(getTotalCashCollection() / 100000).toFixed(2)}L</strong>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px' }}>
                    <strong>Rs. {(getTotalChequeOtherCollection() / 100000).toFixed(2)}L</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Table 3: Cleared and Bounced Cheque */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Cleared and Bounced Cheque
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Branch</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Cheque Cleared</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Cheque Bounced</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chequeStatusData.map((cheque, index) => (
                  <TableRow
                    key={cheque.sno}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {cheque.branch}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center', fontFamily: 'monospace' }}>
                      {cheque.chequeCleared}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center', fontFamily: 'monospace' }}>
                      {cheque.chequeBouncedAmount}
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Total Row */}
                <TableRow sx={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '16px' }}>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', fontFamily: 'monospace', fontSize: '16px' }}>
                    <strong>{getTotalChequeCleared()}</strong>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', fontFamily: 'monospace', fontSize: '16px' }}>
                    <strong>{getTotalChequeBounced()}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default BusinessSummaryReport;
